// import * as Fields from './social.fields';
import { filter, flatMap, get, includes, map, size, some } from "lodash";
import { Platform } from "@linkfluence/models/platform";
import { Project } from "@radarly/models/project";
import { SocialAccount } from "@radarly/models/social_accounts";
import * as Base from "./social.account";
import * as Instagram from "./social.instagram";
import * as Twitter from "./social.twitter";

export function getSocialAccount(platform, account) {
  const fetcher = {
    twitter: Twitter.getAccount,
    instagram: Instagram.getAccount,
  };
  return fetcher[platform]
    ? fetcher[platform](account, platform)
    : Base.getAccount(account, platform);
}

/**
 * Some platforms (e.g. Instagram and Youtube) use credentials from a "parent" platform
 */
const accountCredentialPlatformMatching: Partial<Record<Platform, string>> = {
  instagram: "facebook",
  youtube: "google",
};

const getCredentialsFromPlatform = (
  credentials: Project["credentials"],
  platform: Platform
): Credential[] => {
  return get(
    credentials,
    accountCredentialPlatformMatching[platform] || platform,
    []
  );
};

/**
 * Only keep `credentials` linked to `account`
 */
const filterCredentialFromAccount = (
  account: SocialAccount & { credentialId?: string; credentialIds?: string[] },
  credentials: Project["credentials"]
) => {
  const platformCredentials = getCredentialsFromPlatform(
    credentials,
    account.platform
  );

  return filter(
    platformCredentials,
    (credential) =>
      String(credential.id) === String(account.id) ||
      String(credential.id) === String(account.credentialId) ||
      includes(account.credentialIds, credential.id)
  );
};

/**
 * Does the `account` have at least 1 valid credential
 */
export const hasAccountCredentialError = (
  account: SocialAccount,
  credentials: Project["credentials"]
) => {
  return !some(filterCredentialFromAccount(account, credentials), {
    error: false,
  });
};

export const hasNoValidSocialAccount = (socialAccounts: SocialAccount[]) => {
  return socialAccounts.length === 0 || !some(socialAccounts, { error: false });
};

// Add extra information to a SocialAccount
export const hydrateAccount = (
  account: SocialAccount,
  credentials: Project["credentials"]
) => {
  const error =
    account.relationType === "me" &&
    hasAccountCredentialError(account, credentials);

  return { ...account, error };
};
export const hydrateAccounts = (
  accounts: SocialAccount[],
  credentials: Project["credentials"]
) => {
  return map(accounts, (account) => hydrateAccount(account, credentials));
};

export function getProjectSocialAccounts(
  project: Project,
  platforms: Platform[] = [],
  relations = ["me"]
) {
  const socialAccounts = flatMap(project.socialAccounts);

  const relevantSocialAccounts = filter(
    socialAccounts,
    ({ relationType, platform }) => {
      return (
        includes(relations, relationType) &&
        (!size(platforms) || includes(platforms, platform))
      );
    }
  );
  return map(relevantSocialAccounts, (account) =>
    hydrateAccount(
      getSocialAccount(account.platform, account),
      project.credentials
    )
  );
}
